import { useEffect, useState } from 'react';
import Footer from '~/modules/main-page/FooterMainPage/FooterMainPage.jsx';
import {
  applicationStatus,
  clientApplicationType,
  contragentType,
  initiatorTypeList,
} from '~/constants/directoryList.js';
import { useSelector } from 'react-redux';
import { errorMessages } from '~/constants/errorMessages.js';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { useNavigate } from 'react-router';
import FullPageLoader from '../FullPageLoader/FullPageLoader.tsx';
import { NavigateGroup } from './components/NavigateGroup.tsx';
import { useApplicationReportsForm } from '~/components/ApplicationReports/hooks/useHookForms.ts';
import {
  TApplicationReport,
  TApplicationReportsForm,
} from '~/components/ApplicationReports/types.ts';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Grid, Typography } from '@mui/material';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { styles } from '~/components/ApplicationReports/styles.ts';
import { applicationReport } from '~/store/identification-form/thunks/applicationReportThunks.ts';
import {
  setApplicationExportToExcel,
  setApplicationReportTable,
  setIdentificationError,
} from '~/store/identification-form/slices.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { ManagerRole } from '~/constants/clientTypes.ts';
import { useServicePointsQuery } from '~/queries/useServicePointsQuery.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { selectIdentification } from '~/store/identification-form/selectors.ts';
import { EMPTY_ERROR } from '~/constants/errorCodes.ts';

const ApplicationReports = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { roles } = useGetUserRole();
  const { allServicePoints } = useServicePointsQuery();
  const {
    loading: isLoading,
    error: { errorMessage: error, discard },
  } = useSelector(selectIdentification) || {};
  const [isRequestSuccessful, setIsRequestSuccessful] = useState(false);

  const form = useApplicationReportsForm();
  const { isSubmitting } = form.formState;

  useEffect(() => {
    const isObserverOrOperator = roles?.some((role) =>
      [ManagerRole.observer, ManagerRole.call_center_operator].includes(
        role as unknown as ManagerRole
      )
    );

    if (!isObserverOrOperator) {
      navigate('/services');
    }
  }, [roles]);

  const closeAlertModal = () => {
    discard?.();
    dispatch(setIdentificationError(EMPTY_ERROR));
  };

  const params = { onIsRequestSuccessful: setIsRequestSuccessful };

  const contentForm = isRequestSuccessful ? 'd-none' : '';
  const contentTable = isRequestSuccessful ? '' : 'd-none';

  const onSubmit = async (values: TApplicationReportsForm) => {
    const form = {
      fromDate: values.fromDate,
      toDate: values.toDate,
      status: values.status?.value || null,
      clientType: values.clientType?.value || null,
      applicationType: values.applicationType?.value || null,
      lastUpdatedTo: values.lastUpdatedTo || null,
      toboId: values.toboId?.value || null,
      initiatorType: values.initiatorType?.value || null,
      page: 0,
      size: 50,
    } as TApplicationReportsForm;

    try {
      const res = await dispatch(applicationReport({ form })).unwrap();
      const payload = res as unknown as TApplicationReport;

      if (!payload?.content || !payload.content.length) {
        dispatch(setIdentificationError({ errorMessage: errorMessages.NO_DATA_FOUND }));
        return;
      }
      dispatch(setApplicationExportToExcel(form));
      dispatch(setApplicationReportTable(form));
      setIsRequestSuccessful(true);
    } catch {
      dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={styles.mainPage}>
          <Box sx={styles.containerMainPage}>
            <Grid sx={styles.contentApplicationReports}>
              <Typography variant="h3" sx={{ marginBlockStart: '0em', marginBottom: '10px' }}>
                Первинна/повторна ідентифікація
              </Typography>
              <Box sx={styles.formContainer}>
                {!contentForm && (
                  <Grid container spacing={3}>
                    <FormGridItem>
                      <FormDateInput
                        name="fromDate"
                        label="Дата створення заявки з"
                        placeholder="Введіть або оберіть період з"
                        isCurrentMaxDate
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormDateInput
                        name="toDate"
                        label="Дата створення заявки по"
                        placeholder="Введіть або оберіть період по"
                        isCurrentMaxDate
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormAutocomplete
                        name="status"
                        options={applicationStatus}
                        inputProps={{
                          placeholder: 'Оберіть статус заявки',
                        }}
                        label="Статус заявок"
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormAutocomplete
                        name="clientType"
                        options={contragentType}
                        inputProps={{
                          placeholder: 'Оберіть тип клієнта',
                        }}
                        label="Тип клієнта"
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormAutocomplete
                        name="applicationType"
                        options={clientApplicationType}
                        inputProps={{
                          placeholder: 'Оберіть тип заявки',
                        }}
                        label="Тип заявки"
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormDateInput
                        name="lastUpdatedTo"
                        label="Останні зміни"
                        placeholder="Введіть або оберіть дату останніх змін в бд"
                        isCurrentMaxDate
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormAutocomplete
                        name="toboId"
                        options={allServicePoints}
                        inputProps={{
                          placeholder: 'Оберіть точку обслуговування',
                        }}
                        label="Точка обслуговування"
                      />
                    </FormGridItem>
                    <FormGridItem>
                      <FormAutocomplete
                        name="initiatorType"
                        options={initiatorTypeList}
                        inputProps={{
                          placeholder: 'Оберіть ініціатора заявки',
                        }}
                        label="Ініціатор заявки"
                      />
                    </FormGridItem>
                  </Grid>
                )}
                <NavigateGroup params={params} contentTable={contentTable} />
                {!contentForm && (
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button type="submit" disabled={isSubmitting} variant={'contained'}>
                      Продовжити
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Box>
          <Footer />
        </Box>

        {!isRequestSuccessful && isLoading && <FullPageLoader />}

        <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />
      </form>
    </FormProvider>
  );
};

export default ApplicationReports;
